import type { CacheManifest } from './CacheManifestStore'

export const BROWSER_DURABLE_CACHE_EPOCH = 2
export const BROWSER_DURABLE_CACHE_EPOCH_LOCALSTORAGE_KEY = 'fg_browser_durable_cache_epoch'
export const BROWSER_DURABLE_CACHE_BUDGET_LOCALSTORAGE_KEY = 'fg_browser_durable_cache_budget_bytes'
export const BROWSER_DURABLE_CACHE_DEFAULT_BUDGET_BYTES = 8 * 1024 * 1024 * 1024
export const BROWSER_DURABLE_CACHE_EPOCH_MARKER_PATH = '__fg_browser_cache/epoch.json'
export const RECOVERED_STALE_PROGRESS_STATUS = 'recovered_stale_after_restart'

const INTERRUPTED_CACHE_STATES = new Set(['downloading', 'materializing', 'hydrating', 'building'])
const FUSEGRID_INDEXED_DB_NAMES = [
  'fusegrid_v2_expression_fragments',
  'fusegrid_v2_data_pane_cache',
  'fusegrid_v2_blocks',
  'fusegrid_v2_workbooks',
  'fusegrid_v2_tableau_build_cache',
  'fusegrid_v2_manifests',
  'fusegrid_v2_manifests__healthcheck',
  'fusegrid_v2_tableau',
  'fusegrid_v2_datasource_cache_registry',
  'fusegrid_v2_parity',
  'fusegrid_v2_document_session',
  'fusegrid_v2_creator_collab',
]
// These databases are origin-local FuseGrid execution/cache state. If browser
// storage is in epoch recovery or untracked-budget cleanup, keeping any of them
// around can leave Chromium's per-origin IndexedDB LevelDB store oversized even
// after OPFS artifacts are removed.
const FUSEGRID_DURABLE_CACHE_INDEXED_DB_NAMES = Array.from(new Set(FUSEGRID_INDEXED_DB_NAMES))

export type BrowserCacheClearResult = {
  ok: boolean
  epoch: number
  deleted: number
  bytes_deleted: number
  manifests_deleted: number
  manifest_failed: Array<{ dataset_id: string; error: string }>
  idb_deleted: string[]
  idb_failed: Array<{ name: string; error: string }>
  opfs_failed: Array<{ path: string; error: string }>
}

export type BrowserCacheMaintenanceDeps = {
  closeHandles?: () => Promise<void> | void
  deleteIndexedDbDatabase?: (name: string) => Promise<{ deleted?: boolean; error?: string | null }> | { deleted?: boolean; error?: string | null }
  listIndexedDbDatabaseNames?: () => Promise<string[]> | string[]
  listManifests?: () => Promise<CacheManifest[]> | CacheManifest[]
  deleteManifest?: (datasetId: string) => Promise<void> | void
  onRuntimeCleared?: () => Promise<void> | void
  log?: (event: string, payload?: Record<string, any>) => void
  nowMs?: () => number
  navigatorLike?: any
  timeoutMs?: number
}

type OpfsDeleteStats = {
  deleted: number
  bytesDeleted: number
  failed: Array<{ path: string; error: string }>
}

let maintenancePromise: Promise<void> | null = null
let budgetPrunePromise: Promise<void> | null = null

export const knownFuseGridIndexedDbNames = (): string[] => FUSEGRID_INDEXED_DB_NAMES.slice()
export const knownFuseGridDurableCacheIndexedDbNames = (): string[] => FUSEGRID_DURABLE_CACHE_INDEXED_DB_NAMES.slice()

export const shouldDeleteFuseGridIndexedDbName = (nameRaw: unknown): boolean => {
  const name = String(nameRaw ?? '').trim()
  if (!name) return false
  return FUSEGRID_INDEXED_DB_NAMES.includes(name) || name.startsWith('fusegrid_')
}

export const normalizeBrowserDurableCacheBudgetBytes = (raw: unknown): number => {
  const value = Number(raw)
  if (!Number.isFinite(value) || value <= 0) return BROWSER_DURABLE_CACHE_DEFAULT_BUDGET_BYTES
  return Math.max(64 * 1024 * 1024, Math.floor(value))
}

export const readManifestBrowserCacheEpoch = (manifest: any): number => {
  const raw = manifest && typeof manifest === 'object' && manifest.contracts && typeof manifest.contracts === 'object'
    ? Number(manifest.contracts.browser_cache_epoch)
    : 0
  return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0
}

export const manifestBrowserCacheEpochMismatch = (
  manifest: any,
  expectedEpoch = BROWSER_DURABLE_CACHE_EPOCH,
): boolean => readManifestBrowserCacheEpoch(manifest) !== expectedEpoch

export const recoverInterruptedManifestState = <T extends CacheManifest | any>(
  manifest: T,
  opts?: { nowMs?: number; epoch?: number },
): { manifest: T; changed: boolean; reasons: string[] } => {
  if (!manifest || typeof manifest !== 'object') return { manifest, changed: false, reasons: [] }
  const next: any = { ...manifest }
  const reasons: string[] = []
  const state = String(next.state ?? '').trim().toLowerCase()
  if (INTERRUPTED_CACHE_STATES.has(state)) {
    next.state = 'stale'
    next.progress_status = RECOVERED_STALE_PROGRESS_STATUS
    next.progress_pct = 0
    next.last_progress_at = Number(opts?.nowMs ?? Date.now())
    reasons.push(`state:${state}`)
  }
  if (manifestBrowserCacheEpochMismatch(next, opts?.epoch ?? BROWSER_DURABLE_CACHE_EPOCH)) {
    next.state = 'stale'
    next.progress_status = 'browser_cache_epoch_mismatch'
    next.shared_bases = []
    next.datasource_nodes = []
    reasons.push('browser_cache_epoch')
  }
  return { manifest: next as T, changed: reasons.length > 0, reasons }
}

export type DurableCacheEvictionCandidate = {
  datasetId: string
  bytes: bigint
  lastUsedAt: number
  active?: boolean
}

export const selectDurableCacheEvictionCandidates = (
  input: DurableCacheEvictionCandidate[],
  totalBytes: bigint,
  budgetBytes: bigint,
  activeDatasetIds?: Set<string>,
): DurableCacheEvictionCandidate[] => {
  if (!(budgetBytes > 0n) || totalBytes <= budgetBytes) return []
  const active = activeDatasetIds ?? new Set<string>()
  const out: DurableCacheEvictionCandidate[] = []
  let remaining = totalBytes
  const candidates = input
    .filter((entry) => entry && entry.datasetId && entry.bytes > 0n && !entry.active && !active.has(entry.datasetId))
    .sort((a, b) => a.lastUsedAt - b.lastUsedAt)
  for (const candidate of candidates) {
    if (remaining <= budgetBytes) break
    out.push(candidate)
    remaining = remaining > candidate.bytes ? remaining - candidate.bytes : 0n
  }
  return out
}

export const shouldDeleteFuseGridOpfsEntry = (relativePathRaw: string, kindRaw?: string): boolean => {
  const relativePath = String(relativePathRaw || '').replace(/^\/+/, '')
  const name = relativePath.split('/').filter(Boolean).pop() ?? relativePath
  const lower = relativePath.toLowerCase()
  const kind = String(kindRaw || '').toLowerCase()
  if (!relativePath) return false
  if (relativePath === BROWSER_DURABLE_CACHE_EPOCH_MARKER_PATH) return false
  if (kind === 'directory') {
    return (
      lower === 'tab_execution' ||
      lower.startsWith('tab_execution/') ||
      lower === 'duckdb_tmp' ||
      lower.startsWith('duckdb_tmp/') ||
      lower === 'extracts' ||
      lower.startsWith('extracts/') ||
      lower === 'cache' ||
      lower.startsWith('cache/') ||
      lower.includes('/duckdb_tmp') ||
      lower.includes('/tab_execution')
    )
  }
  return (
    /\.duckdb(?:\.wal)?$/i.test(name) ||
    /\.duckdb\.recovered(?:[_\-.].*)?$/i.test(name) ||
    /\.parquet$/i.test(name) ||
    lower.includes('/duckdb_tmp/') ||
    lower.includes('/tab_execution/')
  )
}

const getNavigator = (deps?: BrowserCacheMaintenanceDeps): any =>
  deps?.navigatorLike ?? (globalThis as any)?.navigator

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), Math.max(1, timeoutMs))
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const getMarkerHandle = async (root: any, create: boolean): Promise<any | null> => {
  const parts = BROWSER_DURABLE_CACHE_EPOCH_MARKER_PATH.split('/').filter(Boolean)
  if (!parts.length) return null
  let dir = root
  for (let i = 0; i < parts.length - 1; i += 1) {
    dir = await dir.getDirectoryHandle(parts[i], { create })
  }
  return await dir.getFileHandle(parts[parts.length - 1], { create })
}

export const readBrowserDurableCacheEpochMarker = async (
  deps?: BrowserCacheMaintenanceDeps,
): Promise<number> => {
  const nav = getNavigator(deps)
  const root = await nav?.storage?.getDirectory?.()
  if (!root) return 0
  try {
    const handle = await getMarkerHandle(root, false)
    if (!handle) return 0
    const file = await handle.getFile()
    const text = await file.text()
    const parsed = JSON.parse(text || '{}')
    const epoch = Number(parsed?.epoch ?? 0)
    return Number.isFinite(epoch) && epoch > 0 ? Math.floor(epoch) : 0
  } catch {
    return 0
  }
}

export const writeBrowserDurableCacheEpochMarker = async (
  epoch = BROWSER_DURABLE_CACHE_EPOCH,
  deps?: BrowserCacheMaintenanceDeps,
): Promise<void> => {
  const nav = getNavigator(deps)
  const root = await nav?.storage?.getDirectory?.()
  if (!root) return
  const handle = await getMarkerHandle(root, true)
  if (!handle) return
  const writable = await handle.createWritable()
  await writable.write(JSON.stringify({ epoch, updated_at: deps?.nowMs?.() ?? Date.now() }))
  await writable.close()
}

const removeOpfsEntry = async (
  parent: any,
  name: string,
  relativePath: string,
  recursive: boolean,
  stats: OpfsDeleteStats,
): Promise<void> => {
  try {
    await parent.removeEntry(name, recursive ? { recursive: true } : undefined)
    stats.deleted += 1
  } catch (error: any) {
    stats.failed.push({ path: relativePath, error: String(error?.message || error || 'delete failed') })
  }
}

const scanAndDeleteFuseGridOpfsArtifacts = async (
  root: any,
  maxDepth = 8,
): Promise<OpfsDeleteStats> => {
  const stats: OpfsDeleteStats = { deleted: 0, bytesDeleted: 0, failed: [] }
  const walk = async (dir: any, prefix: string, depth: number): Promise<void> => {
    if (depth > maxDepth) return
    const entries: Array<[string, any]> = []
    for await (const entry of dir.entries()) entries.push(entry as [string, any])
    for (const [name, handle] of entries) {
      const rel = prefix ? `${prefix}/${name}` : String(name)
      const kind = String(handle?.kind || '')
      if (kind === 'directory') {
        if (shouldDeleteFuseGridOpfsEntry(rel, 'directory')) {
          await removeOpfsEntry(dir, name, rel, true, stats)
          continue
        }
        await walk(handle, rel, depth + 1)
        continue
      }
      if (kind !== 'file') continue
      if (!shouldDeleteFuseGridOpfsEntry(rel, 'file')) continue
      try {
        const file = await handle.getFile?.()
        const size = Number(file?.size ?? 0)
        if (Number.isFinite(size) && size > 0) stats.bytesDeleted += size
      } catch { }
      await removeOpfsEntry(dir, name, rel, false, stats)
    }
  }
  await walk(root, '', 0)
  return stats
}

export const clearBrowserDurableCacheStorage = async (
  deps?: BrowserCacheMaintenanceDeps,
  opts?: { writeEpochMarker?: boolean; deleteManifests?: boolean; deleteIndexedDb?: boolean },
): Promise<BrowserCacheClearResult> => {
  const nav = getNavigator(deps)
  const result: BrowserCacheClearResult = {
    ok: true,
    epoch: BROWSER_DURABLE_CACHE_EPOCH,
    deleted: 0,
    bytes_deleted: 0,
    manifests_deleted: 0,
    manifest_failed: [],
    idb_deleted: [],
    idb_failed: [],
    opfs_failed: [],
  }
  await deps?.closeHandles?.()
  await deps?.onRuntimeCleared?.()

  try {
    const root = await nav?.storage?.getDirectory?.()
    if (root) {
      const opfs = await scanAndDeleteFuseGridOpfsArtifacts(root)
      result.deleted += opfs.deleted
      result.bytes_deleted += opfs.bytesDeleted
      result.opfs_failed.push(...opfs.failed)
    }
  } catch (error: any) {
    result.opfs_failed.push({ path: '/', error: String(error?.message || error || 'OPFS unavailable') })
  }

  const deleteIndexedDb = opts?.deleteIndexedDb === false ? null : deps?.deleteIndexedDbDatabase
  if (deleteIndexedDb) {
    const indexedDbNames = new Set<string>(knownFuseGridDurableCacheIndexedDbNames())
    if (deps?.listIndexedDbDatabaseNames) {
      try {
        const discovered = await deps.listIndexedDbDatabaseNames()
        for (const name of Array.isArray(discovered) ? discovered : []) {
          const normalized = String(name ?? '').trim()
          if (shouldDeleteFuseGridIndexedDbName(normalized)) indexedDbNames.add(normalized)
        }
      } catch (error: any) {
        result.idb_failed.push({ name: '*enumerate*', error: String(error?.message || error || 'indexedDB.databases failed') })
      }
    }
    for (const name of Array.from(indexedDbNames).sort()) {
      try {
        const deleted = await deleteIndexedDb(name)
        if ((deleted as any)?.deleted === false) {
          result.idb_failed.push({ name, error: String((deleted as any)?.error || 'deleteDatabase failed') })
        } else {
          result.idb_deleted.push(name)
        }
      } catch (error: any) {
        result.idb_failed.push({ name, error: String(error?.message || error || 'deleteDatabase threw') })
      }
    }
  }

  if (opts?.deleteManifests === true && deps?.listManifests && deps?.deleteManifest) {
    try {
      const manifests = await deps.listManifests()
      for (const manifest of Array.isArray(manifests) ? manifests : []) {
        const datasetId = String((manifest as any)?.dataset_id ?? '').trim()
        if (!datasetId) continue
        try {
          await deps.deleteManifest(datasetId)
          result.manifests_deleted += 1
        } catch (error: any) {
          result.manifest_failed.push({
            dataset_id: datasetId,
            error: String(error?.message || error || 'manifest delete failed'),
          })
        }
      }
    } catch (error: any) {
      result.manifest_failed.push({
        dataset_id: '*',
        error: String(error?.message || error || 'manifest listing failed'),
      })
    }
  }

  if (opts?.writeEpochMarker !== false) {
    await writeBrowserDurableCacheEpochMarker(BROWSER_DURABLE_CACHE_EPOCH, deps).catch((error: any) => {
      result.opfs_failed.push({ path: BROWSER_DURABLE_CACHE_EPOCH_MARKER_PATH, error: String(error?.message || error || 'marker write failed') })
    })
  }

  result.ok = result.opfs_failed.length === 0 && result.idb_failed.length === 0 && result.manifest_failed.length === 0
  deps?.log?.('browser_cache_clear', {
    ok: result.ok,
    epoch: result.epoch,
    deleted: result.deleted,
    bytes_deleted: result.bytes_deleted,
    manifests_deleted: result.manifests_deleted,
    manifest_failed: result.manifest_failed.length,
    idb_deleted: result.idb_deleted.length,
    idb_failed: result.idb_failed.length,
    idb_failed_names: result.idb_failed.map((entry) => entry.name).slice(0, 8),
    opfs_failed: result.opfs_failed.length,
  })
  return result
}

const runWithMaintenanceLock = async (deps: BrowserCacheMaintenanceDeps | undefined, fn: () => Promise<void>): Promise<void> => {
  const nav = getNavigator(deps)
  const locks = nav?.locks
  const timeoutMs = Math.max(1_000, Number(deps?.timeoutMs ?? 30_000))
  if (locks && typeof locks.request === 'function') {
    await withTimeout(
      locks.request('fg-browser-durable-cache-maintenance', { mode: 'exclusive' }, fn),
      timeoutMs,
      'browser durable cache maintenance lock',
    )
    return
  }
  if (!maintenancePromise) {
    maintenancePromise = fn().finally(() => {
      maintenancePromise = null
    })
  }
  await withTimeout(maintenancePromise, timeoutMs, 'browser durable cache maintenance')
}

export const ensureBrowserDurableCacheEpoch = async (
  deps?: BrowserCacheMaintenanceDeps,
): Promise<{ cleaned: boolean; epoch: number }> => {
  const nav = getNavigator(deps)
  if (!nav?.storage?.getDirectory) return { cleaned: false, epoch: BROWSER_DURABLE_CACHE_EPOCH }
  const existing = await readBrowserDurableCacheEpochMarker(deps)
  if (existing >= BROWSER_DURABLE_CACHE_EPOCH) return { cleaned: false, epoch: existing }

  let cleaned = false
  await runWithMaintenanceLock(deps, async () => {
    const lockedExisting = await readBrowserDurableCacheEpochMarker(deps)
    if (lockedExisting >= BROWSER_DURABLE_CACHE_EPOCH) return
    deps?.log?.('browser_cache_epoch_cleanup_start', {
      stored_epoch: lockedExisting || null,
      target_epoch: BROWSER_DURABLE_CACHE_EPOCH,
    })
    const res = await clearBrowserDurableCacheStorage(deps, {
      writeEpochMarker: true,
      // Epoch safety is about OPFS durable DuckDB/parquet state. IndexedDB cleanup is
      // storage-budget maintenance; keeping it out of the foreground open barrier prevents
      // unrelated IDB close/delete stalls from blocking worksheet renders.
      deleteIndexedDb: false,
    })
    cleaned = true
    if (res.opfs_failed.length > 0) {
      const markerFailures = res.opfs_failed.filter((entry) => entry.path === BROWSER_DURABLE_CACHE_EPOCH_MARKER_PATH)
      if (markerFailures.length > 0) {
        throw new Error(`browser durable cache cleanup failed: opfs_failed=${res.opfs_failed.length} idb_failed=${res.idb_failed.length}`)
      }
      deps?.log?.('browser_cache_epoch_cleanup_opfs_best_effort_failed', {
        target_epoch: BROWSER_DURABLE_CACHE_EPOCH,
        opfs_failed: res.opfs_failed.length,
        opfs_failed_paths: res.opfs_failed.map((entry) => entry.path).slice(0, 8),
      })
    }
    if (res.idb_failed.length > 0) {
      deps?.log?.('browser_cache_epoch_cleanup_idb_best_effort_failed', {
        target_epoch: BROWSER_DURABLE_CACHE_EPOCH,
        idb_failed: res.idb_failed.length,
        idb_failed_names: res.idb_failed.map((entry) => entry.name).slice(0, 8),
      })
    }
    deps?.log?.('browser_cache_epoch_cleanup_complete', {
      target_epoch: BROWSER_DURABLE_CACHE_EPOCH,
      deleted: res.deleted,
      bytes_deleted: res.bytes_deleted,
      idb_deleted: res.idb_deleted.length,
    })
  })
  const epoch = await readBrowserDurableCacheEpochMarker(deps)
  if (epoch < BROWSER_DURABLE_CACHE_EPOCH) {
    throw new Error(`browser durable cache epoch ${BROWSER_DURABLE_CACHE_EPOCH} not established`)
  }
  return { cleaned, epoch }
}

export const shouldEnforceBrowserDurableCacheBudget = async (
  budgetBytes = BROWSER_DURABLE_CACHE_DEFAULT_BUDGET_BYTES,
  deps?: BrowserCacheMaintenanceDeps,
): Promise<{ overBudget: boolean; usageBytes: number; quotaBytes: number }> => {
  const nav = getNavigator(deps)
  const estimate = await nav?.storage?.estimate?.()
  const usageBytes = Number(estimate?.usage ?? 0)
  const quotaBytes = Number(estimate?.quota ?? 0)
  const overBudget = Number.isFinite(usageBytes) && usageBytes > budgetBytes
  return {
    overBudget,
    usageBytes: Number.isFinite(usageBytes) ? Math.max(0, Math.floor(usageBytes)) : 0,
    quotaBytes: Number.isFinite(quotaBytes) ? Math.max(0, Math.floor(quotaBytes)) : 0,
  }
}

export const runCoalescedBrowserBudgetPrune = async (fn: () => Promise<void>): Promise<void> => {
  if (!budgetPrunePromise) {
    budgetPrunePromise = fn().finally(() => {
      budgetPrunePromise = null
    })
  }
  await budgetPrunePromise
}
